import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import type { Compromisso, TipoCompromisso } from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';

type Dados = {
  titulo: string;
  data: string;
  tipo: TipoCompromisso;
  observacao: string | null;
};

type Props = {
  inicial: Compromisso | null;
  dataSugerida?: string;
  aoFechar: () => void;
  aoSalvar: (dados: Dados) => Promise<void>;
};

export function FormularioCompromisso({ inicial, dataSugerida, aoFechar, aoSalvar }: Props) {
  const [titulo, setTitulo] = useState(inicial?.titulo ?? '');
  const [data, setData] = useState(inicial?.data ?? dataSugerida ?? '');
  const [tipo, setTipo] = useState<TipoCompromisso>(inicial?.tipo ?? 'entrega');
  const [observacao, setObservacao] = useState(inicial?.observacao ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  /* Trava de clique duplo. `useState` não serve aqui: o segundo clique pode
     chegar antes do React repintar o botão desabilitado. O ref muda na hora.
     O servidor também se defende — mas a tela não pode contar com isso. */
  const emVoo = useRef(false);

  // Esc fecha, como em qualquer diálogo.
  const guarda = usarFormulario({ titulo, data, tipo, observacao }, aoFechar);
  usarDialogo(guarda.fechar);

  async function enviar(evento: FormEvent | MouseEvent) {
    evento.preventDefault();
    if (emVoo.current) return;

    if (!titulo.trim()) return setErro('Escreva um título.');
    if (!data) return setErro('Escolha uma data.');

    // A gravação só acontece depois do "sim". Ver lib/formulario.ts.
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({
        titulo: titulo.trim(),
        data,
        tipo,
        observacao: observacao.trim() || null,
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (a janela em blocos): o tipo virou três
     botões, e a data em extenso aparece embaixo do campo, como o eco de antes. */
  return (
    <>
      <JanelaEmBlocos
        rotulo="Compromisso"
        sobrelinha={inicial ? 'Calendário · editar compromisso' : 'Calendário · anotar compromisso'}
        titulo={inicial ? 'Editar compromisso' : 'Anotar compromisso'}
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={(e) => void enviar(e)}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Título" obrigatorio col={6}>
            <input id="titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} autoFocus />
          </CampoDoPasso>
          {/* O campo de data do navegador segue o idioma DO NAVEGADOR, não o da
              página: em inglês ele mostra 08/14/2026. A nota embaixo garante que
              a data lida seja sempre a brasileira, sem tirar o calendário nativo. */}
          <CampoDoPasso rotulo="Data" obrigatorio col={2} mono nota={data ? paraTela(data) : undefined}>
            <input id="data" type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </CampoDoPasso>
          <div className="imv-campo c4">
            <span className="imv-rotulo">Tipo</span>
            <div className="imv-seg" role="radiogroup" aria-label="Tipo do compromisso">
              {TIPOS.map(([t, rotulo]) => (
                <button key={t} type="button" role="radio" aria-checked={tipo === t} className={`imv-op neutro${tipo === t ? ' on' : ''}`} onClick={() => setTipo(t)}>
                  <i />
                  {rotulo}
                </button>
              ))}
            </div>
          </div>
          <CampoDoPasso rotulo="Observação" col={6}>
            <textarea id="observacao" rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={2000} />
          </CampoDoPasso>
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="o compromisso"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </>
  );
}

const TIPOS: Array<[TipoCompromisso, string]> = [
  ['entrega', 'Entrega'],
  ['reuniao', 'Reunião'],
  ['interno', 'Interno'],
];
